/**
 * Alpine.js Language Overflow Component
 * Shows only languages that fit on one line with a "+x" indicator for overflow
 */

export function languageOverflow(totalCount) {
    return {
        totalCount,
        visibleCount: totalCount,
        hiddenCount: 0,
        initialized: false,

        init() {
            // Run calculation after DOM is ready
            this.$nextTick(() => {
                this.calculateVisible();
            });

            // Recalculate on window resize
            this._resizeHandler = this.debounce(() => {
                this.calculateVisible();
            }, 150);
            window.addEventListener('resize', this._resizeHandler);
        },

        destroy() {
            if (this._resizeHandler) {
                window.removeEventListener('resize', this._resizeHandler);
            }
        },

        calculateVisible() {
            const container = this.$el;
            if (!container) return;

            const containerWidth = container.offsetWidth;
            const chips = container.querySelectorAll('.lang-chip');
            const overflowBadgeWidth = 45; // Approximate width of "+x" badge
            const gap = 6; // gap-1.5 = 6px

            let usedWidth = 0;
            let visible = 0;

            // First pass: check if all chips fit without overflow badge
            let allFit = true;
            let totalWidth = 0;
            chips.forEach((chip, index) => {
                chip.style.visibility = 'hidden';
                chip.style.position = 'absolute';
                chip.style.display = 'flex';
                totalWidth += chip.offsetWidth + (index > 0 ? gap : 0);
                chip.style.visibility = '';
                chip.style.position = '';
                chip.style.display = '';
            });
            allFit = totalWidth <= containerWidth;

            if (allFit) {
                // All chips fit, show them all
                this.visibleCount = this.totalCount;
                this.hiddenCount = 0;
                this.initialized = true;
                return;
            }

            // Second pass: calculate how many fit with overflow badge
            chips.forEach((chip, index) => {
                chip.style.visibility = 'hidden';
                chip.style.position = 'absolute';
                chip.style.display = 'flex';

                const chipWidth = chip.offsetWidth;

                chip.style.visibility = '';
                chip.style.position = '';
                chip.style.display = '';

                // Always reserve space for overflow badge when not all fit
                const spaceNeeded = usedWidth + chipWidth + (visible > 0 ? gap : 0) + overflowBadgeWidth + gap;

                if (spaceNeeded <= containerWidth) {
                    usedWidth += chipWidth + (visible > 0 ? gap : 0);
                    visible++;
                }
            });

            // Ensure at least one item is visible
            this.visibleCount = Math.max(1, visible);
            this.hiddenCount = Math.max(0, this.totalCount - this.visibleCount);
            this.initialized = true;
        },

        isVisible(index) {
            // Show all until initialized, then only show visible count
            return !this.initialized || index < this.visibleCount;
        },

        debounce(fn, delay) {
            let timeoutId;
            return (...args) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => fn.apply(this, args), delay);
            };
        }
    };
}
